import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Boxes, Search, Soup } from 'lucide-react';
import { rowsOf, useApi } from '@/Hooks/useApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import type { ComponentTarget, RecipeRow } from './recipeTypes';

interface InventoryItemRow {
  id: string;
  name: string;
  sku: string | null;
  unitId: string;
  unit: { id: string; code: string; name: string } | null;
}

interface Props {
  /** The recipe being edited — it may never contain itself. */
  excludeRecipeId: string | null;
  onPick: (target: ComponentTarget) => void;
  onCancel: () => void;
}

function useDebounced(value: string, delay = 300): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Chooses what one BOM line consumes: a stock item **or** a nested sub-recipe.
 *
 * The either/or is the point. `RecipeComponent` carries `inventoryItemId` xor
 * `subRecipeId`, so rather than offering two fields and validating that only
 * one is filled, this offers two lists and hands back a single tagged target.
 * A row therefore cannot be in the illegal state at all.
 */
export function RecipeComponentPicker({ excludeRecipeId, onPick, onCancel }: Props) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'ITEM' | 'SUB'>('ITEM');
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);

  const itemParams = useMemo(
    () => ({ limit: 25, isActive: true, sortBy: 'name', sortOrder: 'asc', ...(debounced ? { search: debounced } : {}) }),
    [debounced],
  );
  const recipeParams = useMemo(
    () => ({ limit: 25, isActive: true, sortBy: 'name', sortOrder: 'asc', ...(debounced ? { search: debounced } : {}) }),
    [debounced],
  );

  const items = useApi<unknown>(kind === 'ITEM' ? '/inventory/items' : null, itemParams);
  const recipes = useApi<unknown>(kind === 'SUB' ? '/recipes' : null, recipeParams);

  const itemRows = useMemo(() => rowsOf<InventoryItemRow>(items.data).rows, [items.data]);
  const recipeRows = useMemo(
    () => rowsOf<RecipeRow>(recipes.data).rows.filter((row) => row.id !== excludeRecipeId),
    [recipes.data, excludeRecipeId],
  );

  const active = kind === 'ITEM' ? items : recipes;

  return (
    <div className="space-y-3 rounded-lg border border-dashed p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={kind === 'ITEM' ? 'default' : 'outline'}
          onClick={() => setKind('ITEM')}
        >
          <Boxes className="h-4 w-4" />
          {t('platform.recipes.editor.ingredient')}
        </Button>
        <Button
          size="sm"
          variant={kind === 'SUB' ? 'default' : 'outline'}
          onClick={() => setKind('SUB')}
        >
          <Soup className="h-4 w-4" />
          {t('platform.recipes.subRecipe')}
        </Button>
        <Button size="sm" variant="ghost" className="ms-auto" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-3.5 h-4 w-4 text-ink-muted" />
        <Input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            kind === 'ITEM'
              ? t('platform.recipes.editor.searchItems')
              : t('platform.recipes.editor.searchRecipes')
          }
          className="ps-9"
        />
      </div>

      {active.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : active.error ? (
        <ErrorState message={active.error} onRetry={active.reload} />
      ) : kind === 'ITEM' ? (
        itemRows.length ? (
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {itemRows.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-start text-sm hover:bg-hover"
                  onClick={() =>
                    onPick({
                      kind: 'ITEM',
                      id: item.id,
                      name: item.name,
                      unitId: item.unit?.id ?? item.unitId,
                      hint: item.sku ?? undefined,
                    })
                  }
                >
                  <span>
                    {item.name}
                    {item.sku && (
                      <span className="ms-2 text-xs text-ink-muted">{item.sku}</span>
                    )}
                  </span>
                  <Badge tone="neutral">{item.unit?.code ?? ''}</Badge>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title={t('platform.recipes.editor.noItems')} />
        )
      ) : recipeRows.length ? (
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {recipeRows.map((recipe) => {
            const yieldUnitId = recipe.activeVersion?.yieldUnitId ?? null;
            return (
              <li key={recipe.id}>
                <button
                  type="button"
                  disabled={!yieldUnitId}
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-start text-sm hover:bg-hover disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() =>
                    onPick({
                      kind: 'SUB',
                      id: recipe.id,
                      name: recipe.name,
                      unitId: yieldUnitId,
                    })
                  }
                >
                  <span>
                    {recipe.name}
                    {!yieldUnitId && (
                      <span className="ms-2 text-xs text-amber-600">
                        {t('platform.recipes.editor.subNeedsActiveVersion')}
                      </span>
                    )}
                  </span>
                  {recipe.activeVersion && <Badge tone="info">v{recipe.activeVersion.version}</Badge>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title={t('platform.recipes.editor.noRecipes')}
          hint={t('platform.recipes.editor.noRecipesHint')}
        />
      )}
    </div>
  );
}
